export const ROLE_OPTIONS = [
  { value: 'super_admin', label: 'Super Admin' },
  { value: 'finance', label: 'Finance' },
  { value: 'admin', label: 'Administrator' },
  { value: 'facility_admin', label: 'Facility Admin' },
  { value: 'pastor', label: 'Pastor' },
  { value: 'marketing', label: 'Marketing' },
  { value: 'intake', label: 'Intake' },
  { value: 'transportation', label: 'Transportation' },
  { value: 'editor', label: 'Editor' },
  { value: 'moderator', label: 'Moderator' },
  { value: 'leader', label: 'Ministry Leader' },
  { value: 'service_planner_admin', label: 'Service Planner Admin' },
  { value: 'service_planner_team_leader', label: 'Service Planner Team Leader' },
  { value: 'service_planner', label: 'Service Planner' },
  { value: 'worship_leader', label: 'Worship Leader' },
  { value: 'teacher', label: 'Teacher' },
  { value: 'student', label: 'Student' },
  { value: 'partner', label: 'Partner' },
  { value: 'visitor', label: 'Visitor' },
] as const;

export type WebRole = (typeof ROLE_OPTIONS)[number]['value'];

export enum Permission {
  VIEW_DASHBOARD = 'view_dashboard',
  VIEW_MARKETING = 'view_marketing',
  VIEW_ANNOUNCEMENTS = 'view_announcements',
  MANAGE_CONTENT = 'manage_content',
  VIEW_SERMON_CONTENT = 'view_sermon_content',
  VIEW_SERMON_QUESTIONS = 'view_sermon_questions',
  MANAGE_EVENTS = 'manage_events',
  SUBMIT_EVENT_REQUESTS = 'submit_event_requests',
  MODERATE_COMMUNITY = 'moderate_community',
  MANAGE_VOLUNTEERS = 'manage_volunteers',
  VIEW_PARTNER_REQUESTS = 'view_partner_requests',
  MANAGE_KIDS_CHURCH = 'manage_kids_church',
  VIEW_USERS = 'view_users',
  MANAGE_USER_ROLES = 'manage_user_roles',
  VIEW_SUPER_ADMIN = 'view_super_admin',
  VIEW_GROWTH = 'view_growth',
  VIEW_GROUP_GOALS = 'view_group_goals',
  MANAGE_SERMON_QUESTIONS = 'manage_sermon_questions',
  VIEW_FINANCE = 'view_finance',
  VIEW_TRANSPORTATION_REQUESTS = 'view_transportation_requests',
  VIEW_CHURCH_PLANNER = 'view_church_planner',
  CREATE_CHURCH_PLAN = 'create_church_plan',
  MANAGE_CHURCH_PLANNER_ROLES = 'manage_church_planner_roles',
  MANAGE_CHURCH_PLAN_ASSIGNMENTS = 'manage_church_plan_assignments',
  MANAGE_FACILITIES = 'manage_facilities',
}

const ROLE_LABELS: Record<WebRole, string> = ROLE_OPTIONS.reduce(
  (acc, role) => ({ ...acc, [role.value]: role.label }),
  {} as Record<WebRole, string>
);

const ROLE_KEY_SET = new Set<WebRole>(ROLE_OPTIONS.map((role) => role.value));
const STAFF_ROLE_SET = new Set<WebRole>([
  'super_admin',
  'finance',
  'admin',
  'facility_admin',
  'pastor',
  'marketing',
  'intake',
  'transportation',
  'editor',
  'moderator',
  'leader',
  'service_planner_admin',
  'service_planner_team_leader',
  'service_planner',
  'worship_leader',
]);

const ADMIN_ROLE_SET = new Set<WebRole>(['super_admin', 'admin', 'pastor']);
const SUPER_ADMIN_ROLE_SET = new Set<WebRole>(['super_admin']);

export const ROLE_PERMISSIONS: Record<WebRole, Permission[]> = {
  super_admin: [
    Permission.VIEW_DASHBOARD,
    Permission.VIEW_MARKETING,
    Permission.MANAGE_CONTENT,
    Permission.VIEW_SERMON_CONTENT,
    Permission.VIEW_SERMON_QUESTIONS,
    Permission.MANAGE_EVENTS,
    Permission.MODERATE_COMMUNITY,
    Permission.MANAGE_VOLUNTEERS,
    Permission.MANAGE_KIDS_CHURCH,
    Permission.VIEW_USERS,
    Permission.MANAGE_USER_ROLES,
    Permission.VIEW_SUPER_ADMIN,
    Permission.VIEW_GROWTH,
    Permission.VIEW_GROUP_GOALS,
    Permission.MANAGE_SERMON_QUESTIONS,
    Permission.VIEW_FINANCE,
    Permission.VIEW_CHURCH_PLANNER,
    Permission.CREATE_CHURCH_PLAN,
    Permission.MANAGE_CHURCH_PLANNER_ROLES,
    Permission.MANAGE_CHURCH_PLAN_ASSIGNMENTS,
    Permission.MANAGE_FACILITIES,
  ],
  finance: [Permission.VIEW_FINANCE],
  admin: [
    Permission.VIEW_DASHBOARD,
    Permission.MANAGE_CONTENT,
    Permission.VIEW_SERMON_CONTENT,
    Permission.VIEW_SERMON_QUESTIONS,
    Permission.MANAGE_EVENTS,
    Permission.MODERATE_COMMUNITY,
    Permission.MANAGE_VOLUNTEERS,
    Permission.MANAGE_KIDS_CHURCH,
    Permission.VIEW_USERS,
    Permission.MANAGE_USER_ROLES,
    Permission.VIEW_GROWTH,
    Permission.VIEW_GROUP_GOALS,
    Permission.MANAGE_SERMON_QUESTIONS,
    Permission.VIEW_CHURCH_PLANNER,
    Permission.CREATE_CHURCH_PLAN,
    Permission.MANAGE_CHURCH_PLANNER_ROLES,
    Permission.MANAGE_CHURCH_PLAN_ASSIGNMENTS,
    Permission.MANAGE_FACILITIES,
  ],
  facility_admin: [Permission.VIEW_DASHBOARD, Permission.MANAGE_FACILITIES],
  pastor: [
    Permission.VIEW_DASHBOARD,
    Permission.VIEW_SERMON_CONTENT,
    Permission.VIEW_SERMON_QUESTIONS,
    Permission.VIEW_GROUP_GOALS,
    Permission.MANAGE_USER_ROLES,
  ],
  marketing: [Permission.VIEW_MARKETING],
  intake: [
    Permission.VIEW_DASHBOARD,
    Permission.VIEW_ANNOUNCEMENTS,
    Permission.VIEW_PARTNER_REQUESTS,
    Permission.MANAGE_KIDS_CHURCH,
    Permission.VIEW_USERS,
  ],
  transportation: [Permission.VIEW_TRANSPORTATION_REQUESTS],
  editor: [Permission.VIEW_DASHBOARD, Permission.MANAGE_CONTENT],
  moderator: [Permission.VIEW_DASHBOARD, Permission.MODERATE_COMMUNITY],
  leader: [Permission.VIEW_DASHBOARD, Permission.SUBMIT_EVENT_REQUESTS, Permission.VIEW_CHURCH_PLANNER],
  service_planner_admin: [
    Permission.VIEW_DASHBOARD,
    Permission.VIEW_CHURCH_PLANNER,
    Permission.CREATE_CHURCH_PLAN,
    Permission.MANAGE_CHURCH_PLANNER_ROLES,
    Permission.MANAGE_CHURCH_PLAN_ASSIGNMENTS,
  ],
  service_planner_team_leader: [Permission.VIEW_DASHBOARD, Permission.VIEW_CHURCH_PLANNER],
  service_planner: [Permission.VIEW_DASHBOARD, Permission.VIEW_CHURCH_PLANNER],
  worship_leader: [Permission.VIEW_DASHBOARD, Permission.VIEW_CHURCH_PLANNER],
  teacher: [Permission.MANAGE_KIDS_CHURCH],
  student: [],
  partner: [],
  visitor: [],
};

const normalizeRoleKey = (role: string): WebRole | null => {
  const cleaned = role.replace(/[\s_-]+/g, '_').toLowerCase().trim();
  if (!cleaned) return null;
  if (cleaned === 'superadmin') return 'super_admin';
  if (cleaned === 'administrator') return 'admin';
  if (cleaned === 'serviceplanneradmin') return 'service_planner_admin';
  if (cleaned === 'serviceplannerteamleader') return 'service_planner_team_leader';
  if (cleaned === 'worshipleader') return 'worship_leader';
  return ROLE_KEY_SET.has(cleaned as WebRole) ? (cleaned as WebRole) : null;
};

export const normalizeRoleKeys = (roles: unknown): WebRole[] => {
  const fromArray = (items: unknown[]) =>
    items
      .filter((role): role is string => typeof role === 'string')
      .map((role) => normalizeRoleKey(role))
      .filter((role): role is WebRole => !!role);

  if (Array.isArray(roles)) {
    return fromArray(roles);
  }
  if (typeof roles === 'string') {
    return fromArray(roles.split(','));
  }
  return [];
};

export const getRoleLabel = (role: string): string => {
  const normalized = normalizeRoleKey(role);
  return normalized ? ROLE_LABELS[normalized] : role;
};

export const hasRole = (roles: unknown, role: WebRole): boolean => {
  const normalized = normalizeRoleKeys(roles);
  return normalized.includes(role);
};

export const hasKidsChurchStaffRole = (roles: unknown): boolean => hasRole(roles, 'teacher');

export const hasAnyRole = (roles: unknown, targetRoles: WebRole[]): boolean => {
  const normalized = normalizeRoleKeys(roles);
  return targetRoles.some((role) => normalized.includes(role));
};

export const hasPermission = (roles: unknown, permission: Permission): boolean => {
  const normalized = normalizeRoleKeys(roles);
  return normalized.some((role) => ROLE_PERMISSIONS[role]?.includes(permission));
};

export const hasAnyPermission = (roles: unknown, permissions: Permission[]): boolean => {
  const normalized = normalizeRoleKeys(roles);
  return normalized.some((role) =>
    permissions.some((permission) => ROLE_PERMISSIONS[role]?.includes(permission))
  );
};

export const hasAllPermissions = (roles: unknown, permissions: Permission[]): boolean => {
  const normalized = normalizeRoleKeys(roles);
  return permissions.every((permission) =>
    normalized.some((role) => ROLE_PERMISSIONS[role]?.includes(permission))
  );
};

export const hasStaffRole = (roles: unknown): boolean => {
  const normalized = normalizeRoleKeys(roles);
  return normalized.some((role) => STAFF_ROLE_SET.has(role));
};

export const hasAdminRole = (roles: unknown): boolean => {
  const normalized = normalizeRoleKeys(roles);
  return normalized.some((role) => ADMIN_ROLE_SET.has(role));
};

export const hasSuperAdminRole = (roles: unknown): boolean => {
  const normalized = normalizeRoleKeys(roles);
  return normalized.some((role) => SUPER_ADMIN_ROLE_SET.has(role));
};
